import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Spline } from 'lucide-react';
import { RESULTADO_LABEL, rutaLinea, tramo } from 'lib/cables';
import { medida as medidaCable } from 'lib/arboles';
import { plural } from 'lib/auditoria';
import { ResultadoDiscreto, MarcaRevision, MarcaCambio } from 'components/ResultadoCable';

// Estado discreto de la ficha: resultado vigente con su punto, o la baja.
export function PuntoEstado({ l }) {
  if (l.activo === false) return <span className="text-xs font-medium text-muted-foreground">Dado de baja</span>;
  return <ResultadoDiscreto resultado={l.resultado} corto textoVacio="Sin inspección" />;
}

const etiquetaAria = (l) => {
  const partes = [`Cable ${l.codigo}`];
  if (l.nombre) partes.push(l.nombre);
  const t = tramo(l);
  if (t) partes.push(t.replace(' → ', ' a '));
  if (l.activo === false) partes.push('dado de baja');
  else partes.push(l.resultado ? `resultado ${String(RESULTADO_LABEL[l.resultado]).toLowerCase()}` : 'sin inspección anual');
  if (l.activo !== false) {
    if (l.vencida) partes.push('revisión pendiente');
    if (l.cambio_vencido) partes.push('cambio vencido');
    if (l.menciones_30d > 0) partes.push(`${plural(l.menciones_30d, 'mención', 'menciones')} en la inspección diaria (30 días)`);
  }
  return partes.join(', ');
};

// Ficha resumida de un cable en el tablero: toda la tarjeta abre su pantalla.
export default function TarjetaCable({ l }) {
  const baja = l.activo === false;
  const t = tramo(l);
  const medidas = [medidaCable(l.longitud_m, 'm'), medidaCable(l.diametro_mm, 'mm')].filter(Boolean).join(' · ');
  return (
    <li data-testid="cable" className="list-none">
      <Link to={rutaLinea(l.id)} aria-label={etiquetaAria(l)}
        className={`group relative flex h-full min-h-[148px] flex-col overflow-hidden rounded-xl border border-border bg-card p-3.5 text-card-foreground
          transition-[border-color,box-shadow,transform] duration-150 ease-out
          [@media(hover:hover)]:hover:border-primary/35 [@media(hover:hover)]:hover:shadow-sm active:scale-[0.985] motion-reduce:transition-none motion-reduce:active:scale-100
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background
          sm:p-4 ${baja ? 'opacity-60' : ''}`}>
        <Spline size={84} strokeWidth={1.1} aria-hidden="true"
          className="pointer-events-none absolute -bottom-4 -right-4 text-primary/[0.06] transition-colors [@media(hover:hover)]:group-hover:text-primary/10" />

        <span className="flex items-start justify-between gap-2">
          <span className="font-heading text-[26px] font-bold leading-none tracking-tight text-foreground tabular-nums sm:text-[28px]">
            {l.codigo}
          </span>
          <span className="pt-1"><PuntoEstado l={l} /></span>
        </span>

        <span className="mt-3 block min-w-0">
          <span className={`block line-clamp-2 break-words text-[15px] font-semibold leading-snug ${t ? 'text-foreground' : 'text-muted-foreground'}`}>
            {t || l.nombre || 'Tramo sin registrar'}
          </span>
          {t && l.nombre && <span className="block truncate text-xs text-muted-foreground">{l.nombre}</span>}
        </span>

        <span className="relative mt-auto block space-y-1 pt-3 text-xs text-muted-foreground">
          <span className="block truncate tabular-nums">{medidas || 'Sin medidas registradas'}</span>
          {l.vencida && !baja && <span className="flex"><MarcaRevision /></span>}
          {l.cambio_vencido && !baja && <span className="flex"><MarcaCambio /></span>}
          {l.menciones_30d > 0 && !baja && (
            <span className="flex items-center gap-1" title="En la inspección diaria de los últimos 30 días">
              <MessageSquare size={12} aria-hidden="true" className="shrink-0" />{plural(l.menciones_30d, 'mención', 'menciones')}
            </span>
          )}
        </span>
      </Link>
    </li>
  );
}
